import React, { useEffect, useState } from "react";
import { X, Loader2, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/site";
import ImageUpload from "../ImageUpload";

const empty = { slug: "", title: "", short: "", category: "", intro: "", includes: [], process: [], image_id: null, image_url: "", order: 100, published: true };

export default function ServiceEditor({ open, service, onClose, onSaved }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [img, setImg] = useState(null);

  useEffect(() => {
    if (service) {
      setForm({ ...empty, ...service });
      setImg(service.image_id ? { id: service.image_id } : (service.image_url ? { url: service.image_url } : null));
    } else { setForm(empty); setImg(null); }
  }, [service, open]);

  if (!open) return null;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        slug: form.slug || slugify(form.title), title: form.title, short: form.short, category: form.category,
        intro: form.intro, includes: form.includes.filter(Boolean), process: form.process.filter(Boolean),
        image_id: img?.id || null, image_url: img?.url && !img?.id ? img.url : (form.image_url || null),
        order: Number(form.order) || 0, published: form.published,
      };
      if (service?.id) await api.patch(`/admin/services/${service.id}`, payload);
      else await api.post(`/admin/services`, payload);
      toast.success(service?.id ? "Tjeneste oppdatert" : "Tjeneste lagt til");
      onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kunne ikke lagre"); }
    finally { setSaving(false); }
  };
  const del = async () => {
    if (!service?.id || !window.confirm("Slette tjenesten?")) return;
    try { await api.delete(`/admin/services/${service.id}`); toast.success("Slettet"); onSaved(); onClose(); }
    catch (e) { toast.error("Kunne ikke slette"); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/60 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sm:hidden pt-2 pb-1 grid place-items-center"><div className="h-1.5 w-10 rounded-full bg-[#D5D0C7]" /></div>
        <div className="p-4 sm:p-5 border-b border-[#E2DDD5] flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="font-display text-lg sm:text-xl font-bold">{service?.id ? "Rediger tjeneste" : "Ny tjeneste"}</h3>
          <button onClick={onClose} className="p-2 -m-2" data-testid="svc-close"><X size={20}/></button>
        </div>
        <div className="p-4 sm:p-5 space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block"><span className="text-[11px] uppercase tracking-wider text-[#555E68]">Tittel</span>
              <input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="mt-1 w-full rounded-md border border-[#D5D0C7] px-3 py-2.5 text-sm" data-testid="svc-title" />
            </label>
            <label className="block"><span className="text-[11px] uppercase tracking-wider text-[#555E68]">Slug (URL)</span>
              <input value={form.slug} onChange={(e) => setForm({...form, slug: e.target.value})} placeholder="tomrer-halden" className="mt-1 w-full rounded-md border border-[#D5D0C7] px-3 py-2.5 text-sm" data-testid="svc-slug" />
            </label>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block"><span className="text-[11px] uppercase tracking-wider text-[#555E68]">Kategori</span>
              <input value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} className="mt-1 w-full rounded-md border border-[#D5D0C7] px-3 py-2.5 text-sm" data-testid="svc-category" />
            </label>
            <label className="block"><span className="text-[11px] uppercase tracking-wider text-[#555E68]">Rekkefølge</span>
              <input type="number" value={form.order} onChange={(e) => setForm({...form, order: e.target.value})} className="mt-1 w-full rounded-md border border-[#D5D0C7] px-3 py-2.5 text-sm" data-testid="svc-order" />
            </label>
          </div>
          <label className="block"><span className="text-[11px] uppercase tracking-wider text-[#555E68]">Kort beskrivelse (kort)</span>
            <input value={form.short} onChange={(e) => setForm({...form, short: e.target.value})} className="mt-1 w-full rounded-md border border-[#D5D0C7] px-3 py-2.5 text-sm" data-testid="svc-short" />
          </label>
          <label className="block"><span className="text-[11px] uppercase tracking-wider text-[#555E68]">Introtekst (detaljsiden)</span>
            <textarea rows={4} value={form.intro} onChange={(e) => setForm({...form, intro: e.target.value})} className="mt-1 w-full rounded-md border border-[#D5D0C7] px-3 py-2.5 text-sm" data-testid="svc-intro" />
          </label>
          <ListEditor label="Hva tjenesten inkluderer" items={form.includes} onChange={(v) => setForm({...form, includes: v})} testid="svc-includes" />
          <ListEditor label="Slik jobber vi" items={form.process} onChange={(v) => setForm({...form, process: v})} testid="svc-process" />
          <ImageUpload label="Bilde" mode="single" scope="service" value={img} onChange={setImg} />
          <label className="block"><span className="text-[11px] uppercase tracking-wider text-[#555E68]">... eller bilde-URL</span>
            <input value={img?.url && !img?.id ? img.url : ""} onChange={(e) => setImg(e.target.value ? { url: e.target.value } : null)} placeholder="https://..." className="mt-1 w-full rounded-md border border-[#D5D0C7] px-3 py-2.5 text-sm" data-testid="svc-image-url" />
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={form.published} onChange={(e) => setForm({...form, published: e.target.checked})} data-testid="svc-published" />
            <span className="text-sm">Publisert på nettsiden</span>
          </label>
        </div>
        <div className="p-4 sm:p-5 border-t border-[#E2DDD5] flex items-center justify-between sticky bottom-0 bg-white">
          {service?.id ? <button onClick={del} className="text-[#8C2F19] font-semibold text-sm inline-flex items-center gap-1.5 hover:underline" data-testid="svc-delete"><Trash2 size={14}/> Slett</button> : <span/>}
          <button onClick={save} disabled={saving || !form.title} className="btn-primary" data-testid="svc-save">{saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>} Lagre</button>
        </div>
      </div>
    </div>
  );
}

function ListEditor({ label, items, onChange, testid }) {
  const setAt = (i, v) => onChange(items.map((x, j) => j === i ? v : x));
  const add = () => onChange([...items, ""]);
  const del = (i) => onChange(items.filter((_, j) => j !== i));
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-[#555E68] mb-1.5">{label}</div>
      <div className="space-y-2">
        {items.map((v, i) => (
          <div key={i} className="flex gap-2">
            <input value={v} onChange={(e) => setAt(i, e.target.value)} className="flex-1 rounded-md border border-[#D5D0C7] px-3 py-2 text-sm" data-testid={`${testid}-${i}`} />
            <button type="button" onClick={() => del(i)} className="px-2 rounded-md border border-[#D5D0C7] text-[#8C2F19]" data-testid={`${testid}-del-${i}`}><X size={14}/></button>
          </div>
        ))}
        <button type="button" onClick={add} className="text-sm font-semibold text-[#1B3626] hover:underline" data-testid={`${testid}-add`}>+ Legg til punkt</button>
      </div>
    </div>
  );
}

function slugify(t) { return (t||"").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g,"").replace(/[^a-z0-9]+/g,"-").replace(/(^-|-$)/g,""); }
